import { debounceTime, Subject } from 'rxjs';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { NotepadItem } from './notepad-item';
import { ScriptNotepad } from './script-notepad';
import { Temporal } from '@js-temporal/polyfill';

export class Notepad {
    id: string;
    title: string;
    description: string;

    created: Temporal.ZonedDateTime;

    private _currentItem?: NotepadItem;

    private _itemMap: Map<string, NotepadItem> = new Map();

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(
        public coords: CoordinatorService,
        public params: any,
    ) {
        this.id = params.id || this.coords.uniqueId();
        this.title = params.title || this.coords.text('newNotepad');
        this.description = params.description || '';
        this.created = params.created
            ? Temporal.ZonedDateTime.from(params.created)
            : this.coords.utils$.zonedDateTimeNow;

        this._markForManifestStore$.subscribe(() => this._manifestStore());

        params.itemLs?.forEach((x: any) => {
            const itm = new NotepadItem(this.coords, this, x);
            this._itemMap.set(itm.id, itm);
        });
    }

    activate() {
        this.coords.notepad$.current = this;
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    delete() {
        this.coords.notepad$.delete(this);
        this._manifestDelete();
        this.coords.display$.markForCheck();
    }

    private _manifestDelete() {
        const ls = [
            {
                id: this.id,
                type: 'notepad',
            },
        ];
        this.coords.manifest$.delete(ls);
    }

    private _manifestStore() {
        console.log('SAVING NOTEPAD MANIFEST', this.id);
        this.coords.manifest$.store({
            type: 'notepad',
            id: this.id,
            valueMap: new Map<string, any>([
                ['title', this.title],
                ['description', this.description],
                ['itemLs', this.itemLs.map((itm) => itm.save())],
            ]),
        });
    }

    json() {
        console.log('JSON NOTEPAD', this.id);
        return {
            id: this.id,
            title: this.title,
            description: this.description,
            created: this.created.toJSON(),
            itemLs: this.itemReadyLs.map((itm) => itm.save()),
        };
    }

    get(id: string) {
        return this._itemMap.get(id);
    }

    get itemReadyLs() {
        return this.itemLs.filter((x) => x.title.trim());
    }

    get itemLs() {
        const ls = Array.from(this._itemMap.values());
        if (!ls.length) {
            ls.push(this.addItem());
        }
        return ls;
    }

    addItem() {
        const itm = new NotepadItem(this.coords, this, {
            parentNotepadId: this.id,
        });
        this._itemMap.set(itm.id, itm);
        this.coords.display$.markForCheck();
        return itm;
    }

    deleteItem(itm: NotepadItem) {
        this._itemMap.delete(itm.id);
        this.coords.display$.markForCheck();
        if (this._currentItem === itm) {
            this.currentItem = this.itemLs[0];
        }
        this.markForManifestStore();
    }

    public get currentItem(): NotepadItem {
        if (!this._currentItem) {
            this._currentItem = this.itemLs[0];
        }
        return this._currentItem;
    }
    public set currentItem(value: NotepadItem) {
        this._currentItem = value;
        this.coords.display$.markForCheck();
    }

    get isInStoryOn() {
        return !this.coords.notepad$.isStoryDisabledNotepad(this);
    }

    set isInStoryOn(value: boolean) {
        this.coords.notepad$.setStoryDisabledNotepad(this, !value);
    }

    get scriptClass() {
        return new ScriptNotepad(this.coords, this);
    }

    get evidenceXitemIdSet() {
        const s = new Set<string>();
        this.itemLs.forEach((ntpItm) =>
            Array.from(ntpItm.evidence.xitemIdSet).forEach((id) => s.add(id)),
        );
        return s;
    }

    dialog() {
        this.coords.notepad$.dialog(this);
    }
}
